import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { api } from '../../lib/api';

type User = {
  id: number; email: string; firstName: string | null; lastName: string | null;
  role: string; isActive: boolean; emailBounced: boolean; createdAt: string;
  company: { name: string } | null;
};

export default function AdminUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [updating, setUpdating] = useState<number | null>(null);

  useEffect(() => {
    api.get<User[]>('/admin/users').then(setUsers).finally(() => setLoading(false));
  }, []);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const matchSearch = !q || u.email.toLowerCase().includes(q) ||
      (u.firstName ?? '').toLowerCase().includes(q) || (u.lastName ?? '').toLowerCase().includes(q) ||
      (u.company?.name ?? '').toLowerCase().includes(q);
    const matchRole = !roleFilter || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  async function toggleRole(u: User) {
    const newRole = u.role === 'admin' ? 'customer' : 'admin';
    if (!confirm(`Change ${u.email} to ${newRole}?`)) return;
    setUpdating(u.id);
    try {
      await api.patch(`/admin/users/${u.id}`, { role: newRole });
      setUsers(p => p.map(x => x.id === u.id ? { ...x, role: newRole } : x));
    } finally { setUpdating(null); }
  }

  async function toggleActive(u: User) {
    setUpdating(u.id);
    try {
      await api.patch(`/admin/users/${u.id}`, { isActive: !u.isActive });
      setUsers(p => p.map(x => x.id === u.id ? { ...x, isActive: !u.isActive } : x));
    } finally { setUpdating(null); }
  }

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold" style={{ color: '#0b0d0e' }}>User accounts</h1>
        <span style={{ fontSize: 13, color: '#8f9b9b' }}>{users.length} total</span>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <input className="input" style={{ maxWidth: 280 }} placeholder="Search by name, email or company…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <select className="input" style={{ maxWidth: 160 }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="">All roles</option>
          <option value="customer">Customer</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      <div className="rounded-xl overflow-hidden" style={{ background: '#fff', border: '1px solid #e5e5e5' }}>
        {loading ? (
          <p style={{ padding: 24, color: '#8f9b9b', fontSize: 14 }}>Loading…</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', minWidth: 620 }}>
              <thead style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                <tr style={{ textAlign: 'left' }}>
                  {['User', 'Company', 'Role', 'Status', 'Joined', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#8f9b9b' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid #fafafa' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <p style={{ fontWeight: 500, color: '#0b0d0e' }}>
                        {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
                      </p>
                      <p style={{ fontSize: 11, color: u.emailBounced ? '#dc2626' : '#8f9b9b' }}>
                        {u.email}{u.emailBounced ? ' ⚠ bounced' : ''}
                      </p>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#374151' }}>{u.company?.name ?? <span style={{ color: '#d1d5db' }}>—</span>}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ background: u.role === 'admin' ? '#fef3c7' : '#f3f4f6', color: u.role === 'admin' ? '#92400e' : '#374151', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999 }}>
                        {u.role}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ background: u.isActive ? '#dcfce7' : '#fee2e2', color: u.isActive ? '#15803d' : '#b91c1c', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999 }}>
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#8f9b9b', fontSize: 12 }}>{u.createdAt.slice(0, 10)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div className="flex items-center gap-3">
                        {(u.role === 'admin' || u.email.toLowerCase().endsWith('@montipower.com')) && (
                          <button onClick={() => toggleRole(u)} disabled={updating === u.id}
                            style={{ fontSize: 12, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                            {u.role === 'admin' ? 'Make customer' : 'Make admin'}
                          </button>
                        )}
                        <button onClick={() => toggleActive(u)} disabled={updating === u.id}
                          style={{ fontSize: 12, color: u.isActive ? '#dc2626' : '#16a34a', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                          {u.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#8f9b9b' }}>No users found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
